import React from 'react'
import { css } from '@emotion/css'
import { useIntl } from 'react-intl'
import { array, number, object, string } from 'yup'
import { debounce, uniqBy } from 'lodash'
import { toast } from '@contrast-security-inc/design-system-alerts'
import { IconExternalLink } from '@contrast-security-inc/design-system-foundations'
import { Stack } from '@contrast-security-inc/design-system-layouts'
import {
  Form,
  FormNumberInput,
  FormTextInput
} from '@contrast-security-inc/design-system-forms'
import { Text } from '@contrast-security-inc/design-system-typography'
import {
  ApiPreviewYaml,
  buildApp,
  codeApiYaml,
  getMetadata,
  PreviewType
} from './api-preview-yaml'
import {
  WizardStepContext,
  WizardStepContextValueType
} from '../agent-wizard-steps'
import {
  useApplicationTags,
  useMetadataFields,
  useEacGroups,
  useRbacGroups,
  useMutateAppRegister
} from '../api'
import { populateCode, useYamlPath } from '../steps/common'
import {
  getCurrentLanguage,
  getLanguageInfo,
  getOrgUuid,
  identity,
  sortByProperty
} from '../common/utils'
import { formatStyles } from '../../../../react-intl-helpers'
import { useApplicationSearchFilter } from '../../../../vulnerability-management/policy/api/use-application-search-filter'
import { useSplitClient } from 'contrastBase/state'
import { Button } from '@contrast-security-inc/design-system-buttons'
import { RegisterApplication } from './register-app'
import {
  Accordion,
  AccordionGroup
} from '@contrast-security-inc/design-system-surfaces'
import { InformationalPill } from '@contrast-security-inc/design-system-badges'
import { useFormContext } from 'react-hook-form'
import { TYPING_DEBOUNCE_MS } from '../common/constants'
import { useOrganizationAgents } from '../../../../keys-settings/api/use-agent-keys'
import { RouteContext } from '../../../../route-provider'
import { KubernetesNoAgentsWarning } from '../steps/kubernetes-no-agents-warning'
import { SingleSelect } from '@contrast-security-inc/design-system-inputs'
import { useAddAgentAuditLog } from '../../../../keys-settings/api/use-agent-logs'
import { AgentKeyAction, AgentKeyType } from '../../../../keys-settings/field'
import { getAgentApiSettings } from '../../../../route'

// Form & Metadata
export const metadataTypeToInputMapping = {
  NUMERIC: { Component: FormNumberInput, schemaType: number() },
  STRING: { Component: FormTextInput, schemaType: string() },
  // POINT_OF_CONTACT subfields
  CONTACT_NAME: { Component: FormTextInput, schemaType: string() },
  EMAIL: { Component: FormTextInput, schemaType: string().email() },
  PHONE: {
    Component: FormTextInput,
    schemaType: string().matches(/^\+?\d{10,14}$/)
  }
}
// dynamcially create schema, metadata fields fetched from the api
export const createAgentConfigFormSchema = (metadataFields) => () => {
  const metadataFieldDefs = metadataFields.reduce(
    (acc, { fieldType, displayLabel, required }) => {
      const { schemaType } = metadataTypeToInputMapping[fieldType]
      const type = required ? schemaType.required() : schemaType

      return { ...acc, [displayLabel]: type }
    },
    {}
  )

  return object({
    name: string().nullable(),
    appGroups: array().of(string()),
    appTags: array().of(string()),
    ...metadataFieldDefs
  })
}

export const useOrgUuid = () => {
  const {
    parameters: [orgUuid]
  } = React.useContext(RouteContext)

  return {
    orgUuid
  }
}

export interface AgentKey {
  id: string
  uid: string
  firstName: string
}

export const saveAuditLogs = (mutate, orgUuid: string, agentUid?: string) => {
  if (agentUid) {
    mutate({
      orgUuid,
      agentUid,
      valueType: AgentKeyType.ApiKey,
      action: AgentKeyAction.View
    })
    mutate({
      orgUuid,
      agentUid,
      valueType: AgentKeyType.ServiceKey,
      action: AgentKeyAction.View
    })
    mutate({
      orgUuid,
      agentUid,
      valueType: AgentKeyType.Token,
      action: AgentKeyAction.View
    })
    mutate({
      orgUuid,
      agentUid,
      valueType: AgentKeyType.Username,
      action: AgentKeyAction.View
    })
  }
}

export const AgentDropdown = () => {
  const orgUuid = useOrgUuid().orgUuid
  const { isLoading, data } = useOrganizationAgents({
    orgUuid,
    filterText: ''
  })

  const ctx: WizardStepContextValueType = React.useContext(WizardStepContext)

  const { mutate } = useAddAgentAuditLog()

  React.useMemo(() => {
    saveAuditLogs(mutate, orgUuid, ctx.apiCredentials.user_uid)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mutate])

  if (isLoading) {
    return <></>
  }

  return (
    <SingleSelect<AgentKey>
      testId="agentDropdown"
      isRequired={true}
      initialSelectedItem={data?.agents[0]}
      items={data?.agents}
      itemToID={(item: AgentKey) => `${item.uid}`}
      onChange={(e) => {
        const agent: any = e.target.value
        const agentUid = agent?.uid
        ctx.updateAgentUid(agentUid)
        saveAuditLogs(mutate, orgUuid, agentUid)
      }}
      itemToString={(item: AgentKey) => (item ? item.firstName : '')}
      label="Agent Key"
    />
  )
}

export const MetadataField = ({ fieldType, displayLabel, required }) => {
  const { Component } = metadataTypeToInputMapping[fieldType]

  return (
    <Component
      name={displayLabel}
      testId={displayLabel}
      key={displayLabel}
      label={displayLabel}
      isRequired={required}
    />
  )
}

const accordionCss = css`
  width: 100%;
  margin: 12px 0;
  overflow: visible;
`

// Hooks (form fields)
export const useApplicationsSearch = (searchTerm) => {
  const { isLoading, error, data } = useApplicationSearchFilter({
    organizationUuid: getOrgUuid(),
    searchTerm
  })

  return {
    isLoading,
    isError: error,
    items: data?.applications?.map((it) => it.appName)
  }
}

export const useEacGroupsSearch = () => {
  const { isLoading, error, data } = useEacGroups({
    organizationUuid: getOrgUuid()
  })

  return {
    isLoading,
    error,
    items: data?.custom_groups?.groups.map((it) => it.name) || []
  }
}

export const useRbacGroupsSearch = () => {
  const { isLoading, error, data } = useRbacGroups({
    organizationUuid: getOrgUuid()
  })

  return {
    isLoading,
    error,
    items: uniqBy(
      data?.accessList
        ?.map((it) => it.resourceGroupName)
        ?.filter((it) => it !== 'ALL_APPLICATIONS_RESOURCE_GROUP') || [],
      identity
    )
  }
}

export const useTagsSearch = () => {
  const { isLoading, error, data } = useApplicationTags({
    organizationUuid: getOrgUuid()
  })

  return {
    isLoading,
    error,
    items: data?.tags
  }
}

export const titleIconCss = css`
  display: flex;
  align-items: center;
  gap: 8px;
`

export const encodeToContentParam = (input: string): string => {
  const utf8Bytes = Buffer.from(input, 'utf-8')

  const byteLength = utf8Bytes.length

  const base64UrlString = base64UrlEncode(utf8Bytes)

  return `${byteLength}@${base64UrlString}`
}

const base64UrlEncode = (bytes: Uint8Array): string => {
  return Buffer.from(bytes).toString('base64')
}

export const getAgentConfigLanguage = (language: string) => {
  if ('DOTNET' === language) {
    return 'dotnet-framework'
  }

  if ('DOTNET_CORE' === language) {
    return 'dotnet-core'
  }

  if ('NODE' === language) {
    return 'nodejs'
  }

  return language.toLowerCase()
}

// TODO https://contrast.atlassian.net/browse/TS-34607
export const customizeEditorButtonLink = (
  url: string,
  language: string,
  content: string
) => {
  const languageParam = getAgentConfigLanguage(language)
  const contentParam = encodeToContentParam(content)
  return `${url}/#mode=share&language=${languageParam}&content=${contentParam}`
}

export const getAppTextForConfigEditor = (app?) => {
  if (!app?.application) {
    return ''
  }

  const nameText = app.application.name ? `name: ${app.application.name}` : ''
  const metadataText = app.application.metadata
    ? `metadata: ${app.application.metadata}`
    : ''

  const applicationLines = [
    nameText && `  ${nameText}`,
    metadataText && `  ${metadataText}`
  ].filter(Boolean)

  return `application:\n${applicationLines.join('\n')}`
}

export const AgentConfig = ({
  isForPipeline = false
}: {
  isForPipeline?: boolean
}) => {
  const intl = useIntl()
  const organizationUuid = getOrgUuid()
  const language = getCurrentLanguage()
  const ctx: WizardStepContextValueType = React.useContext(WizardStepContext)

  const { data } = useMetadataFields({
    organizationUuid
  })

  // prettier-ignore
  const metadataFields = data
    ?.fields
    ?.sort(sortByProperty('required'))
    .reduce((acc, it) => (it.subfields // flatten subfields
      ? [...acc, ...it.subfields]
      : [...acc, it]), []) || []
  // prettier-ignore
  const { mutate: mutateAppRegister } = useMutateAppRegister({
    onSuccess: (data) => {
      ctx.setAppUuid(data?.application?.app_id)
      toast(intl.formatMessage({ id: 'title_success' }))
    },
    onError: (error) => {
      // @ts-ignore
      const errorMessage = error?.data?.messages?.[0] ||
        // @ts-ignore
        error?.data?.errors?.map(it => `${it.field} ${it.message}`).join(',') ||
        intl.formatMessage({ id: 'detail_apiRequestError' })

      toast(errorMessage, { variant: 'error' })
    }
  })
  // prettier-ignore
  const onSubmitSuccess = React.useCallback((data) => {
    mutateAppRegister({ organizationUuid, language, ...data })
    toast(intl.formatMessage({ id: 'title_registeringApplication' }))
  }, [intl, mutateAppRegister, organizationUuid, language])

  return (
    <Form
      createSchema={createAgentConfigFormSchema(metadataFields)}
      // @ts-ignore call sig will be unknown, dynamic form
      onSubmitSuccess={onSubmitSuccess}
    >
      <NormalizedAgentDownloadWizard isForPipeline={isForPipeline} />
    </Form>
  )
}

export const NormalizedAgentDownloadWizard = ({
  isForPipeline
}: {
  isForPipeline: boolean
}) => {
  const intl = useIntl()
  const linkAgentConfigurationEditor = intl.formatMessage({
    id: 'link_agentConfigurationEditor'
  })
  const language = getCurrentLanguage()
  const ctx: WizardStepContextValueType = React.useContext(WizardStepContext)
  const yamlPath = useYamlPath()
  const { isSplitTreatmentOn } = useSplitClient()
  const isMultipleUsersEnabled = isSplitTreatmentOn(
    'PROD-3155_multiple_agent_users'
  )
  const isNormalizedRuby = language === 'RUBY'

  const [openAccordionIndex, setOpenAccordionIndex] = React.useState<
    number | null
  >(isNormalizedRuby ? 2 : 1)

  const handleAccordionToggle = (index: number) => {
    setOpenAccordionIndex((prevIndex) => (prevIndex === index ? null : index))
  }

  const [app, setApp] = React.useState<any>(null)

  const { watch } = useFormContext()

  React.useEffect(() => {
    const formSubscribe = watch(
      debounce((formData) => {
        const newMetadata = getMetadata(formData)

        setApp(buildApp(formData, newMetadata))
      }, TYPING_DEBOUNCE_MS)
    )

    return () => formSubscribe.unsubscribe()
  }, [watch, ctx])

  const isRegisterDetailsExtended =
    language === 'JAVA' || language === 'DOTNET_CORE' || language === 'DOTNET'

  const registerDetailsLink = `https://support.contrastsecurity.com/hc/en-us/articles/${
    language === 'JAVA' ? '360043996031' : '360059554011'
  }`

  const apiConnectionTitle = () => (
    <Stack as="div" role="group" spacing="12px">
      <Text
        font="bodyStrong"
        className={css`
          padding-top: 8px;
        `}
      >
        {intl.formatMessage({ id: 'title_setConnectionToken' })}
      </Text>

      <InformationalPill
        text={intl.formatMessage({ id: 'title_recommended' }).toUpperCase()}
      />

      <Button
        style={{ maxWidth: '300px', marginLeft: 'auto' }}
        variant="secondary"
        Icon={IconExternalLink}
        onClick={(event) => {
          window.open(
            customizeEditorButtonLink(
              linkAgentConfigurationEditor,
              language,
              `api:
  token: ${ctx.apiCredentials.token}
${getAppTextForConfigEditor(app)}`
            ),
            '_blank'
          )
          event.stopPropagation()
        }}
      >
        {intl.formatMessage({
          id: 'title_useConfigurationEditor'
        })}
      </Button>
    </Stack>
  )

  const orgUuid = useOrgUuid().orgUuid
  const { isLoading, data } = useOrganizationAgents({
    orgUuid,
    filterText: ''
  })

  return hasNoAgentUsers(isMultipleUsersEnabled, isLoading, data?.agents) ? (
    <KubernetesNoAgentsWarning stepContentMsg="detail_configureAgentNoAgent" />
  ) : (
    <Stack
      testId="normalizedWizard"
      flexDirection="column"
      className={css`
        margin-top: 16px;
      `}
    >
      <Stack flexDirection="row">
        <Stack
          flexDirection="column"
          className={css`
            width: 100%;
          `}
        >
          {!isForPipeline && (
            <Text font="body" testId="standardWarning">
              {intl.formatMessage(
                {
                  id: 'detail_configureAgentNormalizedWarning'
                },
                {
                  path: yamlPath,
                  version: getLanguageInfo(language)?.priorVersion,
                  ...formatStyles.doclink({
                    path: 'order-of-precedence.html'
                  })
                }
              )}
            </Text>
          )}

          {isMultipleUsersEnabled && (
            <>
              <Text font="body" testId="agentDropdownText">
                {intl.formatMessage(
                  {
                    id: 'detail_configureAgentDropdownText'
                  },
                  {
                    ...formatStyles.link({
                      isExternal: false,
                      href: getAgentApiSettings(orgUuid)
                    })
                  }
                )}
              </Text>
              <AgentDropdown />
            </>
          )}

          <Text
            font="body"
            testId={
              isRegisterDetailsExtended
                ? 'register-application-extended'
                : 'register-application'
            }
          >
            {intl.formatMessage(
              {
                id: isRegisterDetailsExtended
                  ? 'detail_registerApplicationOptionalJava'
                  : 'detail_registerApplicationOptional'
              },
              {
                ...formatStyles.link({
                  href: registerDetailsLink,
                  isExternal: true
                })
              }
            )}
          </Text>
          <Accordion
            className={accordionCss}
            arrowPlacement="left"
            showBorder={false}
            title={
              <Text font="bodyStrong">
                {intl.formatMessage({
                  id: 'title_registerTheApplicationOptional'
                })}
              </Text>
            }
            initialIsOpen={false}
          >
            {() => <RegisterApplication />}
          </Accordion>

          {isForPipeline ? (
            <>
              <Text font="body" testId="pipelineConfiguration">
                {intl.formatMessage(
                  { id: 'detail_configureAgentPipeline' },
                  {
                    ...formatStyles.doclink({
                      path: 'order-of-precedence.html'
                    })
                  }
                )}
              </Text>

              <Accordion
                className={accordionCss}
                arrowPlacement="left"
                showBorder={true}
                title={apiConnectionTitle()}
                initialIsOpen={true}
              >
                {() => (
                  <ApiPreviewYaml
                    previewType={PreviewType.PIPELINE}
                    showDownloadYamlButton={false}
                  />
                )}
              </Accordion>
            </>
          ) : (
            <>
              <Text font="body" testId="standardConfiguration">
                {intl.formatMessage(
                  {
                    id: isNormalizedRuby
                      ? 'detail_configureAgentNormalizedRuby'
                      : 'detail_configureAgentNormalized'
                  },
                  {
                    path: yamlPath,
                    version: getLanguageInfo(language)?.priorVersion,
                    ...formatStyles.doclink({
                      path: 'order-of-precedence.html'
                    })
                  }
                )}
              </Text>

              <AccordionGroup>
                {!isNormalizedRuby && (
                  <Accordion
                    isOpen={openAccordionIndex === 1}
                    setIsOpen={() => handleAccordionToggle(1)}
                    className={accordionCss}
                    arrowPlacement="left"
                    showBorder={true}
                    title={apiConnectionTitle()}
                    initialIsOpen={true}
                    testId={'token-accordion'}
                  >
                    {() =>
                      openAccordionIndex === 1 && (
                        <ApiPreviewYaml
                          previewType={PreviewType.TOKEN}
                          showDownloadYamlButton={false}
                        />
                      )
                    }
                  </Accordion>
                )}

                <Accordion
                  isOpen={openAccordionIndex === 2}
                  setIsOpen={() => handleAccordionToggle(2)}
                  className={accordionCss}
                  arrowPlacement="left"
                  showBorder={true}
                  title={
                    <Stack as="div" role="group" spacing="12px">
                      <Text font="bodyStrong">
                        {intl.formatMessage(
                          {
                            id: isNormalizedRuby
                              ? 'title_setApiKeysNormalize'
                              : 'title_setApiKeysLegacy'
                          },
                          {
                            version: getLanguageInfo(language)?.priorVersion
                          }
                        )}
                      </Text>
                      <Button
                        style={{ maxWidth: '300px' }}
                        className={css`
                          margin-left: auto;
                        `}
                        variant="secondary"
                        Icon={IconExternalLink}
                        onClick={(event) => {
                          window.open(
                            customizeEditorButtonLink(
                              linkAgentConfigurationEditor,
                              language,
                              `${populateCode(codeApiYaml, ctx.apiCredentials)}
                  ${getAppTextForConfigEditor(app)}`
                            ),
                            '_blank'
                          )
                          event.stopPropagation()
                        }}
                      >
                        {intl.formatMessage({
                          id: 'title_useConfigurationEditor'
                        })}
                      </Button>
                    </Stack>
                  }
                  initialIsOpen={false}
                >
                  {() =>
                    openAccordionIndex === 2 && (
                      <ApiPreviewYaml
                        previewType={PreviewType.LEGACY}
                        showDownloadYamlButton={false}
                      />
                    )
                  }
                </Accordion>

                <Accordion
                  isOpen={openAccordionIndex === 3}
                  setIsOpen={() => handleAccordionToggle(3)}
                  className={accordionCss}
                  arrowPlacement="left"
                  showBorder={true}
                  title={
                    <Text font="bodyStrong">
                      {intl.formatMessage({
                        id: 'title_downloadConfiguration'
                      })}
                    </Text>
                  }
                  initialIsOpen={false}
                >
                  {() =>
                    openAccordionIndex === 3 && (
                      <ApiPreviewYaml
                        previewType={PreviewType.CONFIGURATION}
                        showDownloadYamlButton={false}
                      />
                    )
                  }
                </Accordion>
              </AccordionGroup>
            </>
          )}
        </Stack>
      </Stack>
    </Stack>
  )
}

export const hasNoAgentUsers = (multipleAgentUsersEnabled, isLoading, agents) =>
  multipleAgentUsersEnabled && !isLoading && agents.length === 0
